
import { Component, Prop, Vue } from "vue-property-decorator";
import { Getter, namespace, State, Mutation } from "vuex-class";
import { debounce, get } from "lodash";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { CancelTokenSource } from "axios";

import { getDebouncePeriod } from "@/utils/debounce-helper";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";
import { CancellableQuery, FeaturesList, UserGroup, UserPermissions, VueBootstrapField } from "@/store/types";
import {
	DeviceDetails,
	DeviceRecordsRetrievalStatus,
	DeviceType,
	DeviceTypeIdentifier,
	GetDevicesByTypeParams,
	ServerDetails
} from "@/store/devices/types";
import { getNewDeviceDetailsTemplate } from "@/store/devices/templates";
import SureViewIcon from "@/components/SureViewIcon.vue";

const Devices = namespace("devices");

const childDeviceTitles: Record<string, any> = {
	[DeviceTypeIdentifier.Camera]: "Camera",
	[DeviceTypeIdentifier.Audio]: "Audio",
	[DeviceTypeIdentifier.Output]: "Output",
	[DeviceTypeIdentifier.Door]: "Door"
};

const outputSettingsMetaLabel = "Output";

@Component({
	components: {
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class ChildDeviceTable extends Vue {
	@Prop({ required: true, type: Number }) private deviceTypeId: number;
	@Prop({ required: true, default: false, type: Boolean }) private isForAllServers: boolean;
	@Prop({ required: true, default: false, type: Boolean }) private visible;

	@Getter("getPermissions") private permissions: UserPermissions;
	@State private featuresList: FeaturesList;
	@State private UserGroups: UserGroup[];

	@Mutation private setIsLoading: (loading: boolean) => void;

	@Devices.Action private fetchDevicesForAllGroupsByType: (
		params: CancellableQuery<GetDevicesByTypeParams>
	) => Promise<void>;

	@Devices.Mutation private updateCurrentServerRecordsRetrievalStatus: (
		recordsRetrievalStatus: DeviceRecordsRetrievalStatus
	) => void;
	@Devices.Mutation private updateRecordsRetrievalStatus: (
		recordsRetrievalStatus: DeviceRecordsRetrievalStatus
	) => void;
	@Devices.Mutation private setCameraPreviewModalVisibility: (visible: boolean) => void;
	@Devices.Mutation private setCurrentDeviceTypeIdentifier: (deviceTypeIdentifier: DeviceTypeIdentifier) => void;
	@Devices.Mutation private setDeleteChildDeviceModalVisibility: (visible: boolean) => void;
	@Devices.Mutation private setDevice: (device: DeviceDetails) => void;
	@Devices.Mutation private setEditChildDeviceModalVisibility: (visible: boolean) => void;

	@Devices.State private currentServer: ServerDetails;
	@Devices.State private devices: DeviceDetails[];
	@Devices.State private recordsRetrievalStatus: DeviceRecordsRetrievalStatus;
	@Devices.State private serversList: ServerDetails[];

	private axiosCancelTokenSource: CancelTokenSource = null;
	private displayFilteredRecords: boolean = false;
	private filteredRecordsPage: number = 1;
	private loadingMoreRecords: boolean = false;
	private recordsPage: number = 1;
	private recordsPageSize: number = 100;
	private searchTerm: string = "";
	private searchTermPersisted: string = "";
	private tableIsBusy: boolean = false;
	private title: string = "";

	private fields: VueBootstrapField[] = [
		{ key: "title", label: "Title", sortable: true },
		{
			key: "input1",
			label: "Input 1",
			sortable: true
		},
		{
			key: "input2",
			label: "Input 2",
			sortable: true
		},
		{
			key: "extraValue",
			label: "Extra Value",
			sortable: true,
			class: "extra-value-column"
		}
	];

	private async created(): Promise<void> {
		this.title = childDeviceTitles[this.deviceTypeId];

		if (this.includeReferenceShots) {
			this.adjustRecordsPageSizeToMakeResponseQuicker();
		}

		if (this.isForAllServers) {
			this.addAllServersSpecificFields();
		}

		this.addChildSpecificFields();

		if (this.hasEditPermission) {
			this.addEditPermissionsSpecificFields();
		}

		if (this.isForAllServers || (!this.isForAllServers && this.notEnoughRecordsShownForCurrentServer())) {
			await this.getDevices();
		} else if (!this.isForAllServers) {
			//Records may have been added from outside this component, so we need to set the page to prevent needless API requests
			this.recordsPage = Math.floor(this.devicesToDisplay.length / this.recordsPageSize) + 1;
		}
	}

	private get includeReferenceShots(): boolean {
		return this.tableIsForCameras && get(this.featuresList, ["Devices", "ShowReferenceShots"]);
	}

	private get tableIsForCameras(): boolean {
		return this.deviceTypeId === DeviceTypeIdentifier.Camera;
	}

	private adjustRecordsPageSizeToMakeResponseQuicker() {
		this.recordsPageSize = 25;
	}

	private addChildSpecificFields(): void {
		if (this.includeReferenceShots) {
			this.fields.splice(0, 0, {
				key: "preview",
				label: "Preview",
				sortable: false
			});
		}

		if (this.deviceTypeId === DeviceTypeIdentifier.Audio) {
			this.fields.push({
				key: "capabilities",
				label: "Capabilities",
				sortable: true,
				formatter: (value, key, item: DeviceDetails) => {
					return this.getAudioCapability(item);
				},
				sortByFormatted: true
			});
		}
	}

	private addAllServersSpecificFields(): void {
		this.fields.splice(1, 0, {
			key: "serverID",
			label: "Device",
			sortable: true,
			formatter: (value, key, item: DeviceDetails) => {
				return this.getDeviceTitle(item);
			},
			sortByFormatted: true
		});

		this.fields.splice(2, 0, {
			key: "groupID",
			label: "Area",
			sortable: true,
			formatter: groupID => {
				return this.getGroupTitle(groupID);
			},
			sortByFormatted: true
		});
	}

	private get hasEditPermission(): boolean {
		return this.permissions.isAdmin || this.permissions.canEditSiteSetup;
	}

	private get showCameraPreviewIcon(): boolean {
		return this.tableIsForCameras && (this.permissions.isAdmin || this.permissions.canViewCameras);
	}

	private addEditPermissionsSpecificFields(): void {
		this.fields.push({ key: "actions", label: "Actions", sortable: false, class: "text-center" });
	}

	private async getDevices(): Promise<void> {
		this.tableIsBusy = true;

		const queryParams: GetDevicesByTypeParams = {
			deviceTypeId: this.deviceTypeId,
			page: this.recordsPage,
			pageSize: this.recordsPageSize
		};

		if (this.includeReferenceShots) {
			queryParams.includeReferenceShots = true;
		}

		if (!this.isForAllServers) {
			queryParams.serverId = this.currentServer.serverID;
		}

		if (this.searchTerm) {
			queryParams.page = this.filteredRecordsPage;
			queryParams.searchTerm = this.searchTerm;
		}

		if (this.queryIsForRelays) {
			this.adjustQueryForRelays(queryParams);
		}

		this.axiosCancelTokenSource = getNewCancelTokenSource(this.axiosCancelTokenSource, true);

		const cancellableQuery: CancellableQuery<GetDevicesByTypeParams> = {
			params: queryParams,
			cancelTokenSource: this.axiosCancelTokenSource
		};

		await this.fetchDevicesForAllGroupsByType(cancellableQuery);

		this.tableIsBusy = false;
	}

	private get queryIsForRelays(): boolean {
		return this.deviceTypeId === DeviceTypeIdentifier.Output || this.deviceTypeId === DeviceTypeIdentifier.Door;
	}

	private adjustQueryForRelays(queryParams: GetDevicesByTypeParams): void {
		queryParams.selectOutputs = queryParams.deviceTypeId === DeviceTypeIdentifier.Output;
		queryParams.deviceTypeId = DeviceType.Relay;
	}

	private getGroupTitle(groupID: number): string {
		return this.UserGroups.find(ug => ug.groupID == groupID)?.title;
	}

	private getAudioCapability(device: DeviceDetails): string {
		if (device.audioSettings?.transmitEnabled && device.audioSettings?.receiveEnabled) {
			return "2-Way Audio";
		}

		if (device.audioSettings?.transmitEnabled) {
			return "Transmit";
		}

		if (device.audioSettings?.receiveEnabled) {
			return "Receive";
		}

		return "";
	}

	private getDeviceTitle(device: DeviceDetails): string {
		const server = this.serversList.find(s => s.serverID == device.serverID);

		if (server) {
			return server.title;
		}

		return device.serverTitle;
	}

	private notEnoughRecordsShownForCurrentServer(): boolean {
		return (
			this.devices.filter(d => d.deviceTypeID === this.deviceTypeId && d.serverID === this.currentServer.serverID)
				.length < this.recordsPageSize
		);
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		await this.performSearch(searchTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		this.updateRetrievalStatus();

		this.searchTerm = searchTerm;

		if (!this.searchTerm) {
			this.displayFilteredRecords = false;
			return;
		}

		await this.getDevices();

		this.displayFilteredRecords = true;
	}

	private updateRetrievalStatus(): void {
		let newRetrievalStatus: DeviceRecordsRetrievalStatus = {
			...this.recordsRetrievalStatus
		};

		if (!this.isForAllServers) {
			newRetrievalStatus = {
				...this.currentServer.recordsRetrievalStatus
			};
		}

		switch (this.deviceTypeId) {
			case DeviceTypeIdentifier.Camera: {
				newRetrievalStatus.allFilteredCamerasRetrieved = false;
				break;
			}
			case DeviceTypeIdentifier.Audio: {
				newRetrievalStatus.allFilteredAudiosRetrieved = false;
				break;
			}
			case DeviceTypeIdentifier.Door: {
				newRetrievalStatus.allFilteredDoorsRetrieved = false;
				break;
			}
			case DeviceTypeIdentifier.Output: {
				newRetrievalStatus.allFilteredOutputsRetrieved = false;
				break;
			}
		}

		if (this.isForAllServers) {
			this.updateRecordsRetrievalStatus(newRetrievalStatus);
		} else {
			this.updateCurrentServerRecordsRetrievalStatus(newRetrievalStatus);
		}
	}

	private handleAddDevice(): void {
		this.setIsLoading(true);

		const newDevice = getNewDeviceDetailsTemplate();
		newDevice.deviceTypeID = this.deviceTypeId;

		if (this.deviceTypeId === DeviceTypeIdentifier.Door || this.deviceTypeId === DeviceTypeIdentifier.Output) {
			newDevice.deviceTypeID = DeviceType.Relay;
		}

		if (this.currentServer) {
			newDevice.serverID = this.currentServer.serverID;
		}

		this.setDevice(newDevice);
		this.setCurrentDeviceTypeIdentifier(this.deviceTypeId);
		this.setEditChildDeviceModalVisibility(true);
	}

	private get allRecordsInCurrentQueryAreRetrieved(): boolean {
		let recordsRetrievalStatus: DeviceRecordsRetrievalStatus = this.recordsRetrievalStatus;

		if (!this.isForAllServers) {
			recordsRetrievalStatus = this.currentServer.recordsRetrievalStatus;
		}

		switch (this.deviceTypeId) {
			case DeviceTypeIdentifier.Camera: {
				return this.displayFilteredRecords
					? recordsRetrievalStatus.allFilteredCamerasRetrieved
					: recordsRetrievalStatus.allCamerasRetrieved;
			}
			case DeviceTypeIdentifier.Audio: {
				return this.displayFilteredRecords
					? recordsRetrievalStatus.allFilteredAudiosRetrieved
					: recordsRetrievalStatus.allAudiosRetrieved;
			}
			case DeviceTypeIdentifier.Door: {
				return this.displayFilteredRecords
					? recordsRetrievalStatus.allFilteredDoorsRetrieved
					: recordsRetrievalStatus.allDoorsRetrieved;
			}
			case DeviceTypeIdentifier.Output: {
				return this.displayFilteredRecords
					? recordsRetrievalStatus.allFilteredOutputsRetrieved
					: recordsRetrievalStatus.allOutputsRetrieved;
			}
		}

		return false;
	}

	private async handleScrollBarReachEnd(): Promise<void> {
		if (!this.visible || this.tableIsBusy || this.allRecordsInCurrentQueryAreRetrieved || this.loadingMoreRecords) {
			return;
		}

		this.loadingMoreRecords = true;
		this.searchTerm ? this.filteredRecordsPage++ : this.recordsPage++;

		await this.getDevices();

		this.loadingMoreRecords = false;
	}

	private get devicesToDisplay(): DeviceDetails[] {
		let deviceTypeId = this.deviceTypeId;

		if (this.queryIsForRelays) {
			deviceTypeId = DeviceType.Relay;
		}

		const searchTerm = this.searchTerm.toLowerCase();
		const searchTermAsNumber = Number(this.searchTerm);
		const searchNumericFields = !isNaN(searchTermAsNumber);

		return this.devices.filter(
			d =>
				d.deviceTypeID === deviceTypeId &&
				(this.isForAllServers || (!this.isForAllServers && d.serverID === this.currentServer.serverID)) &&
				(!this.displayFilteredRecords ||
					(this.displayFilteredRecords &&
						(d.title.toLowerCase().includes(searchTerm) ||
							(d.extraValue && d.extraValue.toLowerCase().includes(searchTerm)) ||
							(searchNumericFields &&
								(d.input1 === searchTermAsNumber || d.input2 === searchTermAsNumber)) ||
							(this.isForAllServers &&
								(d.serverTitle.toLowerCase().includes(searchTerm) ||
									this.UserGroups.find(g => g.groupID === d.groupID)
										.title.toLowerCase()
										.includes(searchTerm)))))) &&
				(!this.queryIsForRelays ||
					(this.queryIsForRelays &&
						(this.deviceTypeId === DeviceTypeIdentifier.Output
							? d.relaySettings.relayType === outputSettingsMetaLabel
							: d.relaySettings.relayType !== outputSettingsMetaLabel)))
		);
	}

	private handleEditClick(device: DeviceDetails): void {
		this.setIsLoading(true);
		this.setDevice(device);
		this.setCurrentDeviceTypeIdentifier(this.deviceTypeId);
		this.setEditChildDeviceModalVisibility(true);
	}

	private handlePreviewCameraClick(device: DeviceDetails): void {
		this.setDevice(device);
		this.setCameraPreviewModalVisibility(true);
	}

	private handleDeleteClick(device: DeviceDetails): void {
		this.setDevice(device);
		this.setCurrentDeviceTypeIdentifier(this.deviceTypeId);
		this.setDeleteChildDeviceModalVisibility(true);
	}

	private getPreviewImageSource(previewBase64: string): string {
		return `data:image;base64,${previewBase64}`;
	}
}
